import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAdminUsers } from '../../api'; // Adjust path as needed
import { useAuth } from '../../contexts/AuthContext'; // Adjust path as needed
import { UserIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid'; // Removed ArrowLeftIcon
import { Link, useNavigate } from '@tanstack/react-router'; // Use link component and useNavigate
import Breadcrumbs from '../../components/Breadcrumbs'; // Import Breadcrumbs
import Pagination from '../../components/Pagination'; // Import Pagination component

// Helper to get sort icon
const SortIcon = ({ direction }) => {
    if (!direction) return null;
    return direction === 'asc' ? 
        <ChevronUpIcon className="w-4 h-4 inline ml-1" /> : 
        <ChevronDownIcon className="w-4 h-4 inline ml-1" />;
};

// Helper for displaying job counts
const JobCountsDisplay = ({ user }) => {
    const counts = [
        { label: 'Success', count: user.success_count, color: 'text-green-400' },
        { label: 'Failed', count: user.failed_count, color: 'text-red-400' },
        { label: 'Processing', count: user.processing_count, color: 'text-blue-400' },
        { label: 'Pending', count: user.pending_count, color: 'text-yellow-400' },
    ].filter(c => c.count > 0); // Only show statuses with count > 0

    const tooltipText = counts.map(c => `${c.label}: ${c.count}`).join(' | ');

    return (
        <span title={tooltipText} className="cursor-help">
            {user.job_count}
            {counts.length > 0 && (
                 <span className="ml-2 text-xs">
                    ( {counts.map((c, index) => (
                        <span key={c.label} className={c.color}>
                            {c.count}{index < counts.length - 1 ? '/' : ''}
                        </span>
                    ))} )
                </span>
            )}
        </span>
    );
};

const AdminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user: adminUser, startImpersonation } = useAuth();
    const navigate = useNavigate();
    
    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    
    // Sorting state (client-side sorting for current page)
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); // Reset to first page when searching
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const skip = (currentPage - 1) * pageSize;
            const response = await getAdminUsers(pageSize, skip, debouncedSearchTerm);
            setUsers(response.users || []);
            setTotalUsers(response.total || 0);
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setError(err.message || 'Failed to load users.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleImpersonate = (targetUserId) => {
        if (window.confirm(`Are you sure you want to impersonate user ${targetUserId}?`)) {
            // Navigate to dashboard first, then start impersonation
            navigate({ to: '/app/dashboard' }).then(() => {
                startImpersonation(targetUserId);
            });
        }
    };

    // Memoized sorted users (client-side sorting for current page only)
    const sortedUsers = useMemo(() => {
        let sortableUsers = [...users];

        // Sort based on sortConfig
        if (sortConfig.key !== null) {
            sortableUsers.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle date strings for sorting
                if (sortConfig.key === 'created_at') {
                    aValue = aValue ? new Date(aValue).getTime() : 0;
                    bValue = bValue ? new Date(bValue).getTime() : 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableUsers;
    }, [users, sortConfig]);

    // Calculate total pages for pagination
    const totalPages = Math.ceil(totalUsers / pageSize);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const crumbs = [
        { label: 'Admin', to: '/app/admin' },
        { label: 'Users' } // Current page
    ];

    return (
        <div className="text-foreground">
            <Breadcrumbs crumbs={crumbs} />

            {/* Conditional rendering for loading state */}
            {loading && <div className="text-foreground">Loading users...</div>}

            {/* Conditional rendering for error state */}
            {error && <div className="text-danger">Error: {error}</div>}

            {/* Main content shown only when not loading and no error */}
            {!loading && !error && (
                <>
                    <h1 className="text-2xl font-semibold mb-4">Manage users</h1>

                    {/* Search Input */}
                    <div className="mb-4 max-w-xs">
                        <label htmlFor="search-users" className="sr-only">Search users</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                            </div>
                            <input
                                type="text"
                                name="search-users"
                                id="search-users"
                                className="block w-full pl-10 pr-3 py-2 border border-border rounded-md leading-5 bg-background-soft text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent focus:placeholder-muted-foreground sm:text-sm"
                                placeholder="Search by username..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    
                    <div className="bg-background-elevated border border-border shadow-md rounded-lg overflow-auto">
                        <table className="min-w-full leading-normal">
                            <thead>
                                <tr className="bg-background-soft text-left text-muted-foreground uppercase text-sm">
                                    {/* Make headers sortable */}
                                    <th 
                                        className="px-5 py-3 border-b-2 border-border cursor-pointer hover:bg-surface-hover"
                                        onClick={() => requestSort('username')}
                                    >
                                        Username <SortIcon direction={sortConfig.key === 'username' ? sortConfig.direction : null} />
                                    </th>
                                    <th className="px-5 py-3 border-b-2 border-border">User ID</th>
                                    <th 
                                        className="px-5 py-3 border-b-2 border-border cursor-pointer hover:bg-surface-hover"
                                        onClick={() => requestSort('created_at')}
                                    >
                                        Created at <SortIcon direction={sortConfig.key === 'created_at' ? sortConfig.direction : null} />
                                    </th>
                                     <th className="px-5 py-3 border-b-2 border-border">Email verified</th>
                                     <th 
                                        className="px-5 py-3 border-b-2 border-border cursor-pointer hover:bg-surface-hover"
                                        onClick={() => requestSort('job_count')}
                                    >
                                        Total jobs <SortIcon direction={sortConfig.key === 'job_count' ? sortConfig.direction : null} />
                                    </th>
                                    <th className="px-5 py-3 border-b-2 border-border">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-background-soft">
                                        <td className="px-5 py-4 border-b border-border text-sm">
                                            {user.username}
                                        </td>
                                        <td className="px-5 py-4 border-b border-border text-sm text-muted-foreground">
                                            <code>{user.id}</code>
                                        </td>
                                        <td className="px-5 py-4 border-b border-border text-sm">
                                            {user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                                        </td>
                                        <td className="px-5 py-4 border-b border-border text-sm">
                                            {user.email_verified ? 
                                                <span className="text-success">Yes</span> : 
                                                <span className="text-danger">No</span>
                                            }
                                        </td>
                                         <td className="px-5 py-4 border-b border-border text-sm">
                                            <JobCountsDisplay user={user} />
                                        </td>
                                        <td className="px-5 py-4 border-b border-border text-sm space-x-2 whitespace-nowrap">
                                            {adminUser?.id !== user.id && (
                                                <button 
                                                    onClick={() => handleImpersonate(user.id)}
                                                    className="bg-primary hover:bg-primary-hover text-primary-foreground text-xs font-bold py-1 px-2 rounded inline-flex items-center"
                                                    title={`Impersonate ${user.username}`}
                                                >
                                                    <UserIcon className="w-4 h-4 mr-1"/>
                                                    Impersonate
                                                </button>
                                            )}
                                            <Link
                                                to="/app/admin/jobs"
                                                search={{ user_id: user.id, username: user.username }}
                                                className="bg-accent hover:bg-accent/90 text-white text-xs font-bold py-1 px-2 rounded inline-flex items-center"
                                                title={`View jobs for ${user.username}`}
                                            >
                                                 View Jobs
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Show message if no users found */}
                        {users.length === 0 && !loading && (
                            <div className="p-4 text-center text-muted-foreground">
                                {debouncedSearchTerm ? `No users found matching "${debouncedSearchTerm}".` : 'No users found.'}
                            </div>
                        )}
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            paginate={handlePageChange}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default AdminUsersPage; 